import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { Spinner } from "@/components/ui/spinner";
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ImagePlus, Landmark, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { PaymentProfile, PaymentProfileInput } from "@boo/shared";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/ConfirmModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  createPaymentProfile,
  deletePaymentProfile,
  fetchPaymentProfiles,
  fetchProfileQrUrl,
  updatePaymentProfile,
  uploadProfileQr,
} from "@/services/paymentProfileApi";
import { INPUT_CLASS } from "../types/settings.types";
import BillNotificationsCard from "./BillNotificationsCard";

const EMPTY: PaymentProfileInput = {
  label: "",
  bankName: "",
  accountNumber: "",
  accountHolderName: "",
};

function ProfileCard({
  profile,
  onChanged,
  onEdit,
}: {
  profile: PaymentProfile;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fetchedQr, setFetchedQr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Bumped after each upload so the new image is fetched (id/hasQr don't change).
  const [qrVersion, setQrVersion] = useState(0);

  useEffect(() => {
    if (!profile.hasQr) return;
    let url: string | null = null;
    let cancelled = false;
    async function loadQr() {
      try {
        const u = await fetchProfileQrUrl(profile.id, qrVersion);
        url = u;
        if (cancelled) URL.revokeObjectURL(u);
        else setFetchedQr(u);
      } catch {
        setFetchedQr(null);
      }
    }
    void loadQr();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [profile.id, profile.hasQr, qrVersion]);
  const qrUrl = profile.hasQr ? fetchedQr : null;

  async function run(fn: () => Promise<unknown>, fail: string) {
    try {
      await fn();
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : fail);
    }
  }

  return (
    <>
      <Card>
        <CardContent className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            aria-label="Upload QR image"
            aria-busy={uploading}
            className="relative flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border text-muted-foreground"
          >
            {uploading && (
              <span className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 bg-card/80 text-[11px] text-foreground">
                <Spinner className="size-5" />
                Uploading…
              </span>
            )}
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="Receive QR"
                className="size-full object-contain"
              />
            ) : (
              <ImagePlus className="w-5 h-5" />
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              setUploading(true);
              try {
                await uploadProfileQr(profile.id, file);
                setQrVersion((v) => v + 1);
                onChanged();
              } catch (err: unknown) {
                toast.error(
                  err instanceof Error ? err.message : "QR upload failed",
                );
              } finally {
                setUploading(false);
              }
            }}
          />
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-foreground">
                {profile.label}
              </span>
              {profile.isDefault && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                  <Check className="w-3 h-3" /> Default
                </span>
              )}
            </div>
            {profile.bankName && (
              <div className="text-xs text-muted-foreground">
                {profile.bankName}
              </div>
            )}
            {profile.accountNumber && (
              <div className="text-sm text-foreground [font-variant-numeric:tabular-nums]">
                {profile.accountNumber}
              </div>
            )}
            {profile.accountHolderName && (
              <div className="text-xs text-muted-foreground">
                {profile.accountHolderName}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 pt-2">
              {!profile.isDefault && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    run(
                      () =>
                        updatePaymentProfile(profile.id, { isDefault: true }),
                      "Failed to update",
                    )
                  }
                >
                  Make default
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onEdit}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmingDelete(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <ConfirmModal
        open={confirmingDelete}
        danger
        title="Delete payment details?"
        message={`"${profile.label}" will be removed.`}
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmingDelete(false);
          run(() => deletePaymentProfile(profile.id), "Failed to delete");
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}

export default function PaymentProfilesTab() {
  const [profiles, setProfiles] = useState<PaymentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<PaymentProfileInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setProfiles(await fetchPaymentProfiles());
    } catch {
      toast.error("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // async load: state is set after the await, not synchronously
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  function startEdit(profile: PaymentProfile | null) {
    setEditingId(profile?.id ?? "new");
    setForm(
      profile
        ? {
            label: profile.label,
            bankName: profile.bankName ?? "",
            accountNumber: profile.accountNumber ?? "",
            accountHolderName: profile.accountHolderName ?? "",
          }
        : EMPTY,
    );
  }

  async function handleSave(e: { preventDefault(): void }) {
    e.preventDefault();
    if (!form.label.trim()) return;
    setSaving(true);
    try {
      if (editingId === "new") await createPaymentProfile(form);
      else await updatePaymentProfile(editingId!, form);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const field = (key: keyof PaymentProfileInput, placeholder: string) => (
    <input
      value={(form[key] as string | undefined) ?? ""}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      className={`${INPUT_CLASS} w-full py-2`}
    />
  );

  return (
    <div className="space-y-4">
      <BillNotificationsCard />
      <p className="text-sm text-muted-foreground">
        Where friends pay you back. Upload your bank&apos;s receive-QR image and
        optionally an account number; the default profile is used on new bills.
      </p>

      {loading && <ListRowsSkeleton rows={2} variant="cards" />}
      {profiles.map((p) => (
        <ProfileCard
          key={p.id}
          profile={p}
          onChanged={load}
          onEdit={() => startEdit(p)}
        />
      ))}

      {editingId ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Landmark className="w-4 h-4" />
              {editingId === "new" ? "New payment profile" : "Edit profile"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-2">
              {field("label", "Label (e.g. KBank main)")}
              {field("bankName", "Bank name")}
              {field("accountNumber", "Account number")}
              {field("accountHolderName", "Account holder name")}
              <div className="flex gap-2 pt-1">
                <Button
                  type="submit"
                  size="sm"
                  disabled={!form.label.trim()}
                  loading={saving}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </Button>
              </div>
              {editingId === "new" && (
                <p className="text-xs text-muted-foreground">
                  Save first, then tap the QR box to upload your receive-QR.
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button variant="outline" size="sm" onClick={() => startEdit(null)}>
          Add payment profile
        </Button>
      )}
    </div>
  );
}
